'use client';

import React from 'react';
import { useMemo } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';

import { Container } from '@/blocks/components/Container';
import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { SectionsGroup } from '@/blocks/components/SectionsGroup';
import { CenterHeading } from '@/blocks/components/layout';
import typography from '@/styles/typography.module.scss';

import { ConverterCurrency } from '@/app/[lang]/converters/types';

import styles from './styles.module.scss';

const SymbolOverviewNoSSR = dynamic(
  () => import('react-ts-tradingview-widgets').then((w) => w.SymbolOverview),
  {
    ssr: false,
  },
);

function toTradingViewFormat(inputString: string) {
  return inputString.replaceAll('-', '').toUpperCase();
}

type CurrencyChartProps = {
  currencyFrom: ConverterCurrency;
  currencyTo: ConverterCurrency;
};

export const ConvertersChart = (props: CurrencyChartProps) => {
  const tr = useTranslations();
  const { symbol } = useParams();

  return useMemo(
    () => (
      <SectionsGroup>
        <Container>
          <CenterHeading>
            <h2 className={classNames(typography.h2, styles.sectionTitle)}>
              {tr.rich('pages.converters.symbol.sections.tradingView.title', {
                asset1FullName: (_) => <AccentFragment>{props.currencyFrom.title}</AccentFragment>,
                asset2FullName: (_) => <AccentFragment>{props.currencyTo.title}</AccentFragment>,
                asset1ShortName: (_) => props.currencyFrom.description,
                asset2ShortName: (_) => props.currencyTo.description,
              })}
            </h2>
          </CenterHeading>
          <div className={styles.mainCardChart}>
            <SymbolOverviewNoSSR
              gridLineColor={'#E2E2E2'}
              isTransparent={true}
              symbols={[[props.currencyFrom.title, `${toTradingViewFormat(symbol as string)}`]]}
              autosize
              locale={'en'}
              chartOnly={true}
              dateFormat={"dd MMM 'yy"}
            />
          </div>
        </Container>
      </SectionsGroup>
    ),
    [
      props.currencyFrom.description,
      props.currencyFrom.title,
      props.currencyTo.description,
      props.currencyTo.title,
      symbol,
      tr,
    ],
  );
};
